import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOccurrenceAttendance, type OccurrenceAttendancePage } from "@/api/checkin";
import { Card } from "@/components/ui/card";
import { QueryErrorState } from "@/components/ui/QueryErrorState";
import { DataTable, DataTablePagination, SearchBar, type DataTableColumn } from "@/design-system";
import type { ServiceOccurrence } from "@/types/service";

export function ServiceAttendeeRoster({ churchId, service, visitorCount }: {
  churchId: string; service: ServiceOccurrence; visitorCount?: number;
}) {
  const [input, setInput] = useState("");
  const [request, setRequest] = useState({ page: 1, search: "" });
  useEffect(() => {
    const timer = window.setTimeout(() => setRequest({ page: 1, search: input.trim() }), 300);
    return () => window.clearTimeout(timer);
  }, [input]);
  const query = useQuery({
    queryKey: ["occurrence-attendance", churchId, service.id, request.page, request.search],
    queryFn: () => getOccurrenceAttendance(churchId, service.id, request.page, request.search),
  });
  const data = query.data;
  return <Card className="space-y-5 p-6">
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div><h2 className="text-xl font-semibold text-white">Attendance</h2>
        {data && <p className="mt-2 text-3xl font-bold text-emerald-400">{data.present_total} Present</p>}
      </div>
      {visitorCount !== undefined && <p className="text-sm text-slate-400">{visitorCount} Visitors</p>}
    </div>
    <SearchBar value={input} onChange={setInput} placeholder="Search attendees..."/>
    {query.isError ? <QueryErrorState title="Unable to load attendees" description="Try loading the roster again." onRetry={() => void query.refetch()}/> : query.isLoading ?
      <p role="status" className="py-10 text-center text-slate-400">Loading attendees...</p> : data && <>
        <AttendeeTable data={data}/>
        <div className="space-y-3">
          <p className="text-sm text-slate-400" aria-live="polite">{data.items.length ? (data.page - 1) * data.page_size + 1 : 0}–{data.items.length ? (data.page - 1) * data.page_size + data.items.length : 0} of {data.total}</p>
          <DataTablePagination page={data.page} canGoNext={data.page * data.page_size < data.total}
            onPrevious={() => setRequest(current => ({ ...current, page: Math.max(1, current.page - 1) }))}
            onNext={() => setRequest(current => ({ ...current, page: current.page + 1 }))}/>
        </div>
      </>}
  </Card>;
}

export function AttendeeTable({ data }: { data: OccurrenceAttendancePage }) {
  const columns: DataTableColumn<OccurrenceAttendancePage["items"][number]>[] = [
    { key: "member", header: "Member", render: record => record.member_name },
    { key: "status", header: "Status", render: record => <span className="rounded-full bg-emerald-500/10 px-3 py-1 text-emerald-300">{record.status === "PRESENT" ? "Present" : record.status.toLowerCase().replaceAll("_", " ")}</span> },
    { key: "time", header: "Check-in Time", render: record => new Date(record.updated_at || record.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) },
  ];
  return <div className="overflow-x-auto"><DataTable data={data.items} columns={columns}
    emptyTitle={data.total ? "No attendees on this page" : "No attendees found"}
    emptyDescription="Adjust your search or open the check-in roster to record attendance."/></div>;
}
